import { Link } from 'react-router';
import { useBrand } from '../../lib/brand-context.js';
import { useTheme } from '../../lib/theme-context.js';
import type { Theme } from '../../lib/theme-context.js';
import { Segmented } from '../ui/Segmented.js';
import { displayName, initials, roleLabel } from './person.js';

const MENU_ID = 'hub-account-menu';

const THEME_OPTIONS = [
  { value: 'system', label: 'System', icon: 'bx-desktop' },
  { value: 'light', label: 'Light', icon: 'bx-sun' },
  { value: 'dark', label: 'Dark', icon: 'bx-moon' },
] as const satisfies readonly { value: Theme; label: string; icon: string }[];

function close() {
  document.getElementById(MENU_ID)?.hidePopover?.();
}

/**
 * Spec 247 — Kridt's "the bar ends with the same disc, which opens the account
 * menu: profile, theme, sign out."
 *
 * The disc is neutral: people are not status, so never a brand colour. It was
 * `user.picture` — Auth0's generated default, an orange initials image — which
 * the review flagged in four of the seven hub apps.
 *
 * Built on the native `popover` attribute, as Kridt builds its switcher sheet:
 * light-dismiss, Escape and focus return come from the platform rather than
 * from hand-written listeners. Kridt names the menu's contents but ships no CSS
 * for it, so `.hub-account` in primitives.css follows the switcher's rules.
 */
export function AccountMenu() {
  const { user } = useBrand();
  const { theme, setTheme } = useTheme();
  if (!user) return null;

  const name = displayName(user);
  return (
    <>
      <button
        type="button"
        className="hub-avatar"
        popoverTarget={MENU_ID}
        aria-label={`Account: ${name}`}
        data-testid="account-button"
      >
        {initials(name)}
      </button>
      <div id={MENU_ID} popover="auto" className="hub-account" data-testid="account-menu">
        <div className="hub-account__who">
          <span className="hub-person__name">{name}</span>
          <span className="hub-person__role">{roleLabel(user)}</span>
        </div>
        {/* Settings lives here, not in the nav: the review's five-group limit
            counts what an admin sees, and Kridt already puts the person's own
            controls behind this disc. */}
        <Link to="/settings" className="hub-account__item" onClick={close} data-testid="account-settings">
          <i className="bx bx-cog" aria-hidden="true" />
          Settings
        </Link>
        <p className="hub-account__label">Theme</p>
        <div className="hub-account__theme">
          <Segmented options={THEME_OPTIONS} value={theme} onChange={setTheme} label="Theme" testId="account-theme" />
        </div>
        <a href="/auth/logout" className="hub-account__item" data-testid="account-sign-out">
          <i className="bx bx-log-out" aria-hidden="true" />
          Sign out
        </a>
      </div>
    </>
  );
}
